import { useAssistanceFieldContext } from './AssistanceFieldContext.tsx';
import { fieldIsGreen, fieldIsRed, fieldIsYellow } from './utils.tsx';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { useAssistanceContext } from './AssistanceContext.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import * as React from 'react';
import { useState } from 'react';
import Explanation, { legacyExplanationDetails, localizeExplanationDetails } from './Explanation.tsx';
import { uniq } from 'lodash';
import { useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { url } from '../../../core/utils/link.ts';
import { withIcon } from '../../../core/components/Icon.tsx';
import {
    faArrowRight,
    faClipboardList,
    faDatabase,
    faEnvelopeOpenText,
    faExclamationCircle,
    faFile,
    faGear,
    faGearCode,
    faList,
    faListCheck,
    faPlug,
    faQuestionCircle,
    faSparkles,
    faTableRows,
    faUser,
} from '@fortawesome/pro-regular-svg-icons';

const DatabaseIcon = withIcon(faDatabase);
const MagicIcon = withIcon(faSparkles);
const UserIcon = withIcon(faUser);
const DocumentIcon = withIcon(faFile);
const CustomizationIcon = withIcon(faGearCode);
const ErrorIcon = withIcon(faExclamationCircle);
const UnknownIcon = withIcon(faQuestionCircle);
const ArrowRight = withIcon(faArrowRight);
const EmailIcon = withIcon(faEnvelopeOpenText);
const IntegrationIcon = withIcon(faGear);
const RuleIcon = withIcon(faListCheck);
const FormIcon = withIcon(faTableRows);

export const getConfidenceExplanationIcon = (translationKey: any) => {
    if (
        [
            'explanation.master_data_match',
            'explanation.master_data_conversion',
            'explanation.extracted_article_number_and_description',
            'explanation.extracted_article_description',
            'explanation.extracted_article_number',
            'explanation.multiple_good_results',
            'explanation.multiple_good_article_results',
            'explanation.majority_vote_discounted',
        ].includes(translationKey)
    ) {
        return DatabaseIcon;
    } else if (
        ['explanation.historical_matches', 'explanation.historical_matches_with_table'].includes(translationKey)
    ) {
        return MagicIcon;
    } else if (translationKey === 'explanation.user_correction') {
        return UserIcon;
    } else if (['explanation.extracted_value', 'explanation.extracted_value_from_llm'].includes(translationKey)) {
        return DocumentIcon;
    } else if (['explanation.extracted_value_from_email'].includes(translationKey)) {
        return EmailIcon;
    } else if (
        [
            'explanation.decimal_quantity',
            'explanation.out_of_range_validation_error',
            'explanation.agentic_extraction_failed',
        ].includes(translationKey)
    ) {
        return ErrorIcon;
    } else if (translationKey === 'explanation.customization_info') {
        return CustomizationIcon;
    } else if (translationKey === 'explanation.integration') {
        return IntegrationIcon;
    } else if (translationKey === 'explanation.rule_based') {
        return RuleIcon;
    } else if (translationKey === 'explanation.form_submission') {
        return FormIcon;
    } else {
        return UnknownIcon;
    }
};

export const useConfidenceExplanationProps = (
    confidenceExplanation: any,
    dateFormat: string,
    fieldName: string,
    fieldTypeName: any
) => {
    const { t, i18n } = useTranslation('assistance');

    const localizedExplanationDetails = localizeExplanationDetails(
        t,
        confidenceExplanation?.explanationDetails,
        dateFormat,
        i18n
    );
    const [translationKey, explanationDetails] = legacyExplanationDetails(
        confidenceExplanation.translationKey,
        localizedExplanationDetails
    );

    let message: string = translationKey ? t(`${translationKey}`, explanationDetails).toString() : '';
    let rows: [string | string[], string | string[]][] = [];

    const mappingSearchFields = confidenceExplanation?.explanationDetails?.mapping_search_fields;
    const mappingResultFields = confidenceExplanation?.explanationDetails?.mapping_result_fields;
    const relatedDocumentPath = confidenceExplanation?.explanationDetails?.related_document || '';

    const getSearchFieldValues = (fieldKeys: string[]): string[] =>
        uniq(fieldKeys.flatMap((key) => (mappingSearchFields[key] || '').split('\n')).filter(Boolean));
    const getResultFieldValues = (fieldKeys: string[]): string[] =>
        uniq(fieldKeys.flatMap((key) => (mappingResultFields[key] || '').split('\n')).filter(Boolean));

    if (mappingSearchFields && mappingResultFields && translationKey === 'explanation.historical_matches_with_table') {
        if (fieldTypeName === 'CustomerField') {
            rows = [
                // 15. Juli 2025: client_id is legacy; remove after a while when old documents are assisted
                [mappingSearchFields.client_id || mappingSearchFields.source_client_id, mappingResultFields.client_id],
                [
                    getSearchFieldValues([
                        'sender_name',
                        'delivery_receiver_name',
                        'invoice_receiver_name',
                        'contact_name',
                    ]),
                    mappingResultFields.name,
                ],
                [
                    getSearchFieldValues(['sender_address', 'delivery_address', 'invoice_address']),
                    mappingResultFields.address,
                ],
            ];
        } else if (fieldTypeName === 'ContactField') {
            rows = [
                [mappingSearchFields.contact_id, mappingResultFields.contact_id],
                [mappingSearchFields.name, mappingResultFields.name],
                [mappingSearchFields.email, mappingResultFields.email],
                [mappingSearchFields.phone, mappingResultFields.phone],
            ];
        } else if (fieldTypeName === 'AddressField') {
            rows = [
                [mappingSearchFields.address_id, mappingResultFields.address_id],
                [mappingSearchFields.name, mappingResultFields.name],
                [mappingSearchFields.misc, mappingResultFields.misc],
                [mappingSearchFields.street_and_nr, mappingResultFields.street_and_nr],
                [mappingSearchFields.company_name, mappingResultFields.company_name],
                [mappingSearchFields.postcode, mappingResultFields.postcode],
                [mappingSearchFields.city, mappingResultFields.city],
                [mappingSearchFields.country, mappingResultFields.country],
                [mappingSearchFields.email, mappingResultFields.email],
                [mappingSearchFields.phone, mappingResultFields.phone],
            ];
        } else if (fieldName === 'unit') {
            // Unit mapping
            rows = [
                [mappingSearchFields.unit, mappingResultFields.unit],
                [mappingSearchFields.article_number, ''],
                [mappingSearchFields.article_description, ''],
            ];
        } else {
            // Fallback for article number field
            rows = [
                [
                    getSearchFieldValues(['supplier_article_number', 'debtor_article_number', 'ean']),
                    getResultFieldValues([
                        'article_partition_specific_number',
                        'article_number',
                        'article_number_2',
                        'article_number_3',
                    ]),
                ],
                [mappingSearchFields.article_description, ''],
                [mappingSearchFields.unit, ''],
            ];
        }
    }

    return { message, rows, relatedDocumentPath, Icon: getConfidenceExplanationIcon(translationKey) };
};

const ConfidenceExplanationMappingValue = ({ cell }: { cell: string | string[] }) => {
    const { t } = useTranslation('assistance');

    const value = Array.isArray(cell) ? cell[0] : cell;
    const otherValues = Array.isArray(cell) ? cell.slice(1) : [];
    const [showAll, setShowAll] = useState(false);

    return (
        <div className="whitespace-pre-line font-medium align-top">
            <p>{value || <em>({t('explanationTable.noValue')})</em>}</p>

            {otherValues.length > 0 ? (
                !showAll ? (
                    <p>
                        <a
                            className="font-normal italic hover:underline cursor-pointer"
                            onClick={() => setShowAll(true)}
                        >
                            {t('explanationTable.andOthers', { count: otherValues.length })}
                        </a>
                    </p>
                ) : (
                    otherValues.map((value: string, index: number) => <p key={index}>{value}</p>)
                )
            ) : null}
        </div>
    );
};

const ConfidenceExplanationMappingTable = React.memo(
    ({ rows, severity }: { rows: [string | string[], string | string[]][]; severity?: string }) => {
        const { t } = useTranslation('assistance');

        const borderClassName = classnames(
            'border-line-strong',
            severity === 'success' && 'border-line-success-strong',
            severity === 'warning' && 'border-line-warning-strong',
            severity === 'error' && 'border-line-danger-strong'
        );

        const searchValues = rows
            .map((row) => row[0])
            .filter((value) => (Array.isArray(value) ? value.length > 0 && value[0] : value));
        if (searchValues.length === 0) searchValues.push('');

        const resultValues = rows
            .map((row) => row[1])
            .filter((value) => (Array.isArray(value) ? value.length > 0 && value[0] : value));
        if (resultValues.length === 0) resultValues.push('');

        return (
            <table className="w-full table-fixed">
                <tbody>
                    <tr>
                        <td className="whitespace-pre-line pb-2 align-top">{t('explanationTable.extracted')}:</td>
                        <td className="w-10 whitespace-pre-line align-top text-center relative" rowSpan={2}>
                            <div className="flex h-full flex-col overflow-auto absolute items-center gap-2 w-10">
                                <div className={classnames('w-0 flex-1 border-l', borderClassName)} />
                                <ArrowRight />
                                <div className={classnames('w-0 flex-1 border-l', borderClassName)} />
                            </div>
                        </td>
                        <td className="whitespace-pre-line align-top">{t('explanationTable.learned')}:</td>
                    </tr>
                    <tr>
                        <td className="align-top">
                            <div className="flex flex-col gap-2">
                                {searchValues.map((value, index) => (
                                    <ConfidenceExplanationMappingValue key={index} cell={value} />
                                ))}
                            </div>
                        </td>
                        <td className="align-top">
                            <div className="flex flex-col gap-2">
                                {resultValues.map((value, index) => (
                                    <ConfidenceExplanationMappingValue key={index} cell={value} />
                                ))}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        );
    }
);

const ConfidenceExplanationRelatedDocument = React.memo(({ relatedDocumentPath }: { relatedDocumentPath: string }) => {
    const { t } = useTranslation('assistance');
    const { documentConfiguration } = useAssistanceContext();

    const [_, recordId] = relatedDocumentPath.split('/');

    // apollo takes care of caching
    const { data: relatedDocumentData } = useQuery(documentConfiguration.GET_RELATED_DOCUMENT, {
        variables: { id: recordId },
    });
    const relatedDocument = relatedDocumentData?.[documentConfiguration?.documentTypeName + 'ProcessingRecord'];

    return (
        <div>
            {relatedDocument && (
                <span>
                    {t('explanation.relatedDocument', { date: format(relatedDocument?.assistedAt, 'P') })}{' '}
                    <a
                        href={url(documentConfiguration.ASSISTANCE_PATH, { recordId })}
                        target="_blank"
                        className="underline hover:opacity-70 transition-all"
                    >
                        {relatedDocument?.subject || t('explanation.document')}
                    </a>
                </span>
            )}
        </div>
    );
});

const ConfidenceExplanation = ({
    message,
    rows,
    relatedDocumentPath,
}: {
    message: string;
    rows?: [string | string[], string | string[]][];
    relatedDocumentPath?: string;
}) => {
    const { data, config, value } = useAssistanceFieldContext();

    const severity = fieldIsGreen({ data, config, value }, { includeMatching: false })
        ? 'success'
        : fieldIsYellow({ data, config, value }, { includeMatching: false })
          ? 'warning'
          : fieldIsRed({ data, config, value }, { includeMatching: false })
            ? 'error'
            : '';

    return (
        <Explanation severity={severity}>
            {message && <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message) }} />}
            {rows && rows.length > 0 && <ConfidenceExplanationMappingTable rows={rows} severity={severity} />}
            {relatedDocumentPath && <ConfidenceExplanationRelatedDocument relatedDocumentPath={relatedDocumentPath} />}
        </Explanation>
    );
};

export default ConfidenceExplanation;
